import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import "./InstanceManager.css";

const ACCESS_PASSWORD = "instance777!";

const instances = [
  {
    id: "elite-toronto",
    name: "Elite Toronto",
    city: "Toronto, ON",
    plan: "Enterprise",
    status: "Healthy",
    mrr: 12400,
    lifetime: 182000,
    lastPayment: "2024-08-21",
    usage: 92,
    marker: { top: 28, left: 57 },
    region: "Canada",
    industry: "Promo + Decor",
  },
  {
    id: "elite-vancouver",
    name: "Elite Vancouver",
    city: "Vancouver, BC",
    plan: "Growth",
    status: "Healthy",
    mrr: 6400,
    lifetime: 83450,
    lastPayment: "2024-08-18",
    usage: 76,
    marker: { top: 30, left: 18 },
    region: "Canada",
    industry: "Decoration",
  },
  {
    id: "elite-chicago",
    name: "Elite Chicago",
    city: "Chicago, IL",
    plan: "Scale",
    status: "At Risk",
    mrr: 9100,
    lifetime: 121900,
    lastPayment: "2024-08-12",
    usage: 58,
    marker: { top: 40, left: 50 },
    region: "USA",
    industry: "Promo",
  },
  {
    id: "elite-dallas",
    name: "Elite Dallas",
    city: "Dallas, TX",
    plan: "Growth",
    status: "Healthy",
    mrr: 5200,
    lifetime: 67990,
    lastPayment: "2024-08-20",
    usage: 70,
    marker: { top: 55, left: 44 },
    region: "USA",
    industry: "Screenprint",
  },
  {
    id: "elite-los-angeles",
    name: "Elite Los Angeles",
    city: "Los Angeles, CA",
    plan: "Scale",
    status: "Healthy",
    mrr: 11200,
    lifetime: 142300,
    lastPayment: "2024-08-22",
    usage: 88,
    marker: { top: 52, left: 16 },
    region: "USA",
    industry: "Promo + Web",
  },
  {
    id: "elite-new-york",
    name: "Elite New York",
    city: "New York, NY",
    plan: "Enterprise",
    status: "Healthy",
    mrr: 16800,
    lifetime: 210500,
    lastPayment: "2024-08-23",
    usage: 95,
    marker: { top: 42, left: 73 },
    region: "USA",
    industry: "Promo + Ops",
  },
  {
    id: "elite-mexico-city",
    name: "Elite Mexico City",
    city: "Mexico City, MX",
    plan: "Launch",
    status: "Needs Review",
    mrr: 2800,
    lifetime: 11200,
    lastPayment: "2024-07-30",
    usage: 43,
    marker: { top: 70, left: 40 },
    region: "Mexico",
    industry: "Promo",
  },
];

const billingHistory = [
  {
    id: "inv-2024-08",
    date: "Aug 1, 2024",
    amount: "$78,300",
    status: "Paid",
    notes: "Annual platform + usage overage.",
  },
  {
    id: "inv-2024-07",
    date: "Jul 1, 2024",
    amount: "$65,120",
    status: "Paid",
    notes: "Quarterly onboarding for 2 new instances.",
  },
  {
    id: "inv-2024-06",
    date: "Jun 1, 2024",
    amount: "$59,450",
    status: "Paid",
    notes: "SaaS subscription renewals.",
  },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

function InstanceManager() {
  const [enteredPassword, setEnteredPassword] = useState("");
  const [authorized, setAuthorized] = useState(
    localStorage.getItem("ec_instance_manager_auth") === "true"
  );
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(instances[0].id);
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [instanceOptions, setInstanceOptions] = useState([]);
  const [membershipInstanceId, setMembershipInstanceId] = useState("");
  const [membershipStatus, setMembershipStatus] = useState("");
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipRemovingId, setMembershipRemovingId] = useState(null);

  const handleLogin = (event) => {
    event.preventDefault();
    if (enteredPassword === ACCESS_PASSWORD) {
      setAuthorized(true);
      localStorage.setItem("ec_instance_manager_auth", "true");
    }
  };

  const handleLogout = () => {
    setAuthorized(false);
    setEnteredPassword("");
    localStorage.removeItem("ec_instance_manager_auth");
  };

  const hoveredInstance = instances.find((instance) => instance.id === hoveredId);
  const selectedInstance = instances.find((instance) => instance.id === selectedId);

  useEffect(() => {
    if (!authorized) return;
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;

    axios
      .get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/instances`, {
        headers: { accessToken },
      })
      .then((response) => {
        setInstanceOptions(response.data?.listOfPosts || []);
      })
      .catch((error) => {
        console.error("Failed to load instances:", error);
      });
  }, [authorized]);

  useEffect(() => {
    if (!authorized) return;
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;

    const timer = setTimeout(() => {
      axios
        .get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/instance-memberships`, {
          headers: { accessToken },
          params: { query: userQuery, limit: 25 },
        })
        .then((response) => {
          const users = response.data?.users || [];
          setUserResults(users);
          if (selectedUser) {
            const refreshed = users.find((user) => user.id === selectedUser.id);
            if (refreshed) setSelectedUser(refreshed);
          }
        })
        .catch((error) => {
          console.error("Failed to load user memberships:", error);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [authorized, userQuery, selectedUser?.id]);

  const kpis = useMemo(() => {
    const totalMrr = instances.reduce((sum, instance) => sum + instance.mrr, 0);
    const totalLifetime = instances.reduce(
      (sum, instance) => sum + instance.lifetime,
      0
    );
    const healthyCount = instances.filter((instance) => instance.status === "Healthy").length;
    const averageUsage = Math.round(
      instances.reduce((sum, instance) => sum + instance.usage, 0) / instances.length
    );
    return [
      {
        label: "Portfolio MRR",
        value: formatCurrency(totalMrr),
        trend: "+12.4% MoM",
      },
      {
        label: "Customer Lifetime Value",
        value: formatCurrency(totalLifetime),
        trend: "+8.1% QoQ",
      },
      {
        label: "Healthy Instances",
        value: `${healthyCount}/${instances.length}`,
        trend: "2 flagged",
      },
      {
        label: "Platform Adoption",
        value: `${averageUsage}%`,
        trend: "Weekly active",
      },
    ];
  }, []);

  const handleMembershipAssign = async () => {
    if (!selectedUser || !membershipInstanceId) {
      setMembershipStatus("Select a user and an instance to assign.");
      return;
    }

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      setMembershipStatus("Missing access token.");
      return;
    }

    setMembershipLoading(true);
    setMembershipStatus("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/${selectedUser.id}/instance-memberships`,
        { addInstanceIds: [Number(membershipInstanceId)] },
        { headers: { accessToken } }
      );

      const instances = response.data?.instances || [];
      setSelectedUser((prev) => (prev ? { ...prev, instances } : prev));
      setMembershipStatus("Instance assigned.");
    } catch (error) {
      console.error("Failed to assign instance:", error);
      setMembershipStatus("Failed to assign instance.");
    } finally {
      setMembershipLoading(false);
    }
  };

  const handleMembershipRemove = async (instanceId) => {
    if (!selectedUser || !instanceId) return;

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      setMembershipStatus("Missing access token.");
      return;
    }

    setMembershipRemovingId(instanceId);
    setMembershipStatus("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/${selectedUser.id}/instance-memberships`,
        { removeInstanceIds: [Number(instanceId)] },
        { headers: { accessToken } }
      );

      const instances = response.data?.instances || [];
      setSelectedUser((prev) => (prev ? { ...prev, instances } : prev));
      setMembershipStatus("Instance removed.");
    } catch (error) {
      console.error("Failed to remove instance:", error);
      setMembershipStatus("Failed to remove instance.");
    } finally {
      setMembershipRemovingId(null);
    }
  };

  if (!authorized) {
    return (
      <div className="instance-manager instance-manager--locked">
        <div className="instance-manager__auth-card">
          <p className="instance-manager__eyebrow">Elite-Core Administrator Access</p>
          <h1>Instance Manager</h1>
          <p className="instance-manager__subtitle">
            This view is reserved for elite-core-administrator access. Enter
            the temporary password to continue.
          </p>
          <form onSubmit={handleLogin} className="instance-manager__auth-form">
            <label htmlFor="instance-manager-password">Access password</label>
            <input
              id="instance-manager-password"
              type="password"
              value={enteredPassword}
              onChange={(event) => setEnteredPassword(event.target.value)}
              placeholder="instance777!"
            />
            <button type="submit">Unlock Dashboard</button>
            <p className="instance-manager__auth-hint">
              Security roadmap: add MFA, hardware key requirements, IP
              allowlists, audit logging, and dedicated elite-core-administrator
              accounts.
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="instance-manager">
      <header className="instance-manager__header">
        <div>
          <p className="instance-manager__eyebrow">
            Elite Core • Elite-Core Administrator View
          </p>
          <h1>Instance Manager</h1>
          <p className="instance-manager__subtitle">
            A unified control plane for every Elite Core instance with revenue
            signals, operational health, and billing visibility.
          </p>
        </div>
        <button className="instance-manager__logout" onClick={handleLogout}>
          Sign out
        </button>
      </header>
      <section className="instance-manager__kpis">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="instance-manager__kpi-card">
            <p>{kpi.label}</p>
            <h3>{kpi.value}</h3>
            <span>{kpi.trend}</span>
          </div>
        ))}
      </section>
      <section className="instance-manager__main">
        <div className="instance-manager__map-panel">
          <div className="instance-manager__map-header">
            <div>
              <h2>Instance map view</h2>
              <p>Hover an instance to reveal the location marker.</p>
            </div>
            <span className="instance-manager__map-badge">North America</span>
          </div>
          <div className="instance-manager__map">
            <svg viewBox="0 0 800 420" aria-hidden="true">
              <path
                d="M86 88 L146 56 L248 60 L320 74 L366 60 L414 70 L468 54 L532 70 L596 96 L676 136 L712 198 L664 242 L612 234 L582 252 L558 292 L516 286 L484 320 L434 300 L376 318 L330 300 L298 316 L254 288 L224 240 L194 232 L168 188 L118 166 L96 124 Z"
                fill="url(#mapGradient)"
                stroke="#2a405f"
                strokeWidth="4"
              />
              <defs>
                <linearGradient id="mapGradient" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0%" stopColor="#18263b" />
                  <stop offset="100%" stopColor="#223852" />
                </linearGradient>
              </defs>
            </svg>
            {instances.map((instance) => {
              const isHovered = instance.id === hoveredId;
              return (
                <div
                  key={instance.id}
                  className={`instance-manager__marker ${
                    isHovered ? "instance-manager__marker--active" : ""
                  }`}
                  style={{
                    top: `${instance.marker.top}%`,
                    left: `${instance.marker.left}%`,
                  }}
                >
                  <span />
                  <span className="instance-manager__marker-dot" />
                </div>
              );
            })}
            {hoveredInstance && (
              <div
                className="instance-manager__tooltip"
                style={{
                  top: `${hoveredInstance.marker.top - 20}%`,
                  left: `${hoveredInstance.marker.left + 6}%`,
                }}
              >
                <div className="instance-manager__tooltip-header">
                  <div>
                    <strong>{hoveredInstance.name}</strong>
                    <span>{hoveredInstance.city}</span>
                  </div>
                  <span className="instance-manager__tooltip-pill">
                    {hoveredInstance.status}
                  </span>
                </div>
                <div className="instance-manager__tooltip-grid">
                  <div>
                    <p>Plan</p>
                    <span>{hoveredInstance.plan}</span>
                  </div>
                  <div>
                    <p>Region</p>
                    <span>{hoveredInstance.region}</span>
                  </div>
                  <div>
                    <p>MRR</p>
                    <span>{formatCurrency(hoveredInstance.mrr)}</span>
                  </div>
                  <div>
                    <p>Lifetime</p>
                    <span>{formatCurrency(hoveredInstance.lifetime)}</span>
                  </div>
                  <div>
                    <p>Adoption</p>
                    <span>{hoveredInstance.usage}%</span>
                  </div>
                  <div>
                    <p>Last Pay</p>
                    <span>{hoveredInstance.lastPayment}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <aside className="instance-manager__list-panel">
          <h2>Active instances</h2>
          <div className="instance-manager__list">
            {instances.map((instance) => (
              <button
                key={instance.id}
                type="button"
                className={`instance-manager__list-item ${
                  selectedId === instance.id
                    ? "instance-manager__list-item--selected"
                    : ""
                }`}
                onMouseEnter={() => setHoveredId(instance.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => setSelectedId(instance.id)}
              >
                <div>
                  <h3>{instance.name}</h3>
                  <p>{instance.city}</p>
                </div>
                <div className="instance-manager__list-meta">
                  <span>{instance.plan}</span>
                  <span className={`status-${instance.status.replace(/\s/g, "").toLowerCase()}`}>
                    {instance.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <div className="instance-manager__actions">
            <button type="button">Open Console</button>
            <button type="button">View Billing</button>
            <button type="button" className="instance-manager__danger">
              Pause Instance
            </button>
          </div>
        </aside>
      </section>
      <section className="instance-manager__memberships">
        <div className="instance-manager__memberships-header">
          <div>
            <h2>User instance access</h2>
            <p>Search every user, review their instance memberships, and add or remove access.</p>
          </div>
        </div>
        <div className="instance-manager__memberships-grid">
          <div className="instance-manager__memberships-panel">
            <label htmlFor="instance-membership-search">Search users</label>
            <input
              id="instance-membership-search"
              type="text"
              placeholder="Search by name or email"
              value={userQuery}
              onChange={(event) => setUserQuery(event.target.value)}
            />
            <div className="instance-manager__membership-list">
              {userResults.map((user) => (
                <button
                  key={user.id}
                  className={`instance-manager__membership-user${
                    selectedUser?.id === user.id ? " is-active" : ""
                  }`}
                  type="button"
                  onClick={() => {
                    setSelectedUser(user);
                    setMembershipStatus("");
                  }}
                >
                  <div>
                    <strong>{user.first_name} {user.last_name}</strong>
                    <span>{user.email}</span>
                  </div>
                  <span className="instance-manager__membership-count">
                    {(user.instances || []).length} instance{(user.instances || []).length === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
              {!userResults.length && (
                <p className="instance-manager__membership-empty">No users found.</p>
              )}
            </div>
          </div>
          <div className="instance-manager__memberships-panel">
            <h3>Selected user</h3>
            {selectedUser ? (
              <>
                <div className="instance-manager__membership-selected">
                  <div>
                    <strong>{selectedUser.first_name} {selectedUser.last_name}</strong>
                    <span>{selectedUser.email}</span>
                  </div>
                  <span>{selectedUser.role}</span>
                </div>
                <div className="instance-manager__membership-tags">
                  {(selectedUser.instances || []).map((instance) => (
                    <span key={instance.id} className="instance-manager__membership-tag">
                      <span>
                        {instance.instanceName || instance.instanceUniqueId || `Instance ${instance.id}`}
                      </span>
                      <button
                        type="button"
                        className="instance-manager__membership-remove"
                        onClick={() => handleMembershipRemove(instance.id)}
                        disabled={membershipRemovingId === instance.id}
                        aria-label={`Remove ${instance.instanceName || instance.instanceUniqueId || `Instance ${instance.id}`}`}
                      >
                        {membershipRemovingId === instance.id ? "Removing..." : "Remove"}
                      </button>
                    </span>
                  ))}
                  {!selectedUser.instances?.length && (
                    <span className="instance-manager__membership-empty">
                      No memberships yet.
                    </span>
                  )}
                </div>
                <div className="instance-manager__membership-actions">
                  <select
                    value={membershipInstanceId}
                    onChange={(event) => setMembershipInstanceId(event.target.value)}
                  >
                    <option value="">Select an instance</option>
                    {instanceOptions.map((instance) => (
                      <option key={instance.id} value={instance.id}>
                        {instance.instanceName || instance.instanceUniqueId || `Instance ${instance.id}`}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={handleMembershipAssign} disabled={membershipLoading}>
                    {membershipLoading ? "Assigning..." : "Assign instance"}
                  </button>
                </div>
                {membershipStatus && (
                  <p className="instance-manager__membership-status">{membershipStatus}</p>
                )}
              </>
            ) : (
              <p className="instance-manager__membership-empty">
                Select a user to view their instance access.
              </p>
            )}
          </div>
        </div>
      </section>
      {selectedInstance && (
        <section className="instance-manager__details">
          <div className="instance-manager__details-card">
            <h2>{selectedInstance.name}</h2>
            <p>{selectedInstance.city}</p>
            <div className="instance-manager__detail-grid">
              <div>
                <span>Current MRR</span>
                <strong>{formatCurrency(selectedInstance.mrr)}</strong>
              </div>
              <div>
                <span>Lifetime value</span>
                <strong>{formatCurrency(selectedInstance.lifetime)}</strong>
              </div>
              <div>
                <span>Last payment</span>
                <strong>{selectedInstance.lastPayment}</strong>
              </div>
              <div>
                <span>Adoption</span>
                <strong>{selectedInstance.usage}%</strong>
              </div>
              <div>
                <span>Industry</span>
                <strong>{selectedInstance.industry}</strong>
              </div>
              <div>
                <span>Region</span>
                <strong>{selectedInstance.region}</strong>
              </div>
            </div>
          </div>
          <div className="instance-manager__details-card">
            <h2>Billing history</h2>
            <div className="instance-manager__billing">
              {billingHistory.map((bill) => (
                <div key={bill.id} className="instance-manager__billing-row">
                  <div>
                    <strong>{bill.date}</strong>
                    <p>{bill.notes}</p>
                  </div>
                  <div>
                    <span>{bill.amount}</span>
                    <em>{bill.status}</em>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default InstanceManager;
